/**
 * Dati società (V45): nome e logo modificabili; i limiti restano in sola
 * lettura, con il consumo attuale, perché li alza solo il gestore della
 * piattaforma.
 */

import { type FormEvent, useState } from 'react';
import { modificaDatiSocietaSchema } from '@calcio/shared';
import { OctagonXIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';

interface Rapporto {
  usato: number;
  massimo: number;
}

interface Dati {
  nome: string;
  logoUrl: string | null;
  limiti: {
    squadreStagione: Rapporto;
    giocatori: Rapporto;
    account: Rapporto;
    assistente: Rapporto;
  };
}

export function DatiSocieta() {
  const { dati, errore, ricarica } = useDati<Dati>('/amministrazione/societa');
  if (!dati)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );
  return <Form dati={dati} onSalvato={ricarica} />;
}

function Form({ dati, onSalvato }: { dati: Dati; onSalvato: () => Promise<void> }) {
  const [nome, setNome] = useState(dati.nome);
  const [logoUrl, setLogoUrl] = useState(dati.logoUrl ?? '');
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = modificaDatiSocietaSchema.safeParse({ nome, logoUrl });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama('/amministrazione/societa', { metodo: 'PUT', corpo: esito.data });
      await onSalvato();
      toast.success('Dati della società salvati.');
    } catch (errore) {
      setErroreServer(messaggio(errore));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div>
      <Testata titolo="Dati società" voce="dati-societa" />
      <Corpo className="flex max-w-2xl flex-col gap-8">
        <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
          <FieldGroup>
            <Field data-invalid={Boolean(errori.nome) || undefined}>
              <FieldLabel htmlFor="nome">Nome</FieldLabel>
              <Input
                id="nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                aria-invalid={Boolean(errori.nome) || undefined}
              />
              {errori.nome && <FieldError>{errori.nome}</FieldError>}
            </Field>
            <Field data-invalid={Boolean(errori.logoUrl) || undefined}>
              <FieldLabel htmlFor="logoUrl">Logo</FieldLabel>
              <Input
                id="logoUrl"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://"
                aria-invalid={Boolean(errori.logoUrl) || undefined}
              />
              <FieldDescription>
                Indirizzo dell'immagine. Lascia vuoto per toglierlo.
              </FieldDescription>
              {errori.logoUrl && <FieldError>{errori.logoUrl}</FieldError>}
            </Field>
          </FieldGroup>
          {dati.logoUrl && (
            <img src={dati.logoUrl} alt={`Logo di ${dati.nome}`} className="h-16 w-auto" />
          )}
          {erroreServer && (
            <Alert variant="destructive">
              <OctagonXIcon />
              <AlertTitle>Salvataggio non riuscito</AlertTitle>
              <AlertDescription>{erroreServer}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <PulsanteInvio type="submit" inCorso={inCorso}>
              Salva
            </PulsanteInvio>
          </div>
        </form>

        <FieldSet>
          <FieldLegend>Limiti</FieldLegend>
          <FieldDescription>
            I limiti non si modificano da qui: per alzarli contatta il gestore della piattaforma.
          </FieldDescription>
          <FieldGroup>
            <RigaLimite etichetta="Squadre per stagione" valore={dati.limiti.squadreStagione} />
            <RigaLimite etichetta="Anagrafiche" valore={dati.limiti.giocatori} />
            <RigaLimite etichetta="Account" valore={dati.limiti.account} />
            <RigaLimite
              etichetta="Domande all'assistente nel mese"
              valore={dati.limiti.assistente}
            />
          </FieldGroup>
        </FieldSet>
      </Corpo>
    </div>
  );
}

function RigaLimite({ etichetta, valore }: { etichetta: string; valore: Rapporto }) {
  return (
    <Field orientation="horizontal">
      <FieldLabel>{etichetta}</FieldLabel>
      <p className="text-sm tabular-nums">
        {valore.usato} / {valore.massimo}
      </p>
    </Field>
  );
}
